import { isMobile } from '../../../../helpers';
import { BoostedWheelTheme } from '../../boostedJackpotModule/boostedJackpot/partnerWheelTheme';

const webSceneSpineResources = {
  wheelIcon: {
    name: 'wheelIcon',
    path: 'frame/frameModuleResources/boostedJackpot/wheelThemes/default/wheelIcon/web/wheelIcon.json',
    config: {
      metadata: {
        spineAtlasFile: 'frame/frameModuleResources/boostedJackpot/wheelThemes/default/wheelIcon/web/wheelIcon.atlas',
      },
    },
  },
};

const mobileSceneSpineResources = {
  wheelIcon: {
    name: 'wheelIcon',
    path: 'frame/frameModuleResources/boostedJackpot/wheelThemes/default/wheelIcon/mobile/wheelIcon.json',
    config: {
      metadata: {
        spineAtlasFile: 'frame/frameModuleResources/boostedJackpot/wheelThemes/default/wheelIcon/mobile/wheelIcon.atlas',
      },
    },
  },
};

const webAtlases = {
  boostedJackpotIconInfoBoard: {
    name: 'boostedJackpotIconInfoBoard',
    path: 'atlases/web/boostedJackpotIconBoard.json?v=20260513-1',
  },
};

const mobileAtlases = {
  boostedJackpotIconInfoBoard: {
    name: 'boostedJackpotIconInfoBoard',
    path: 'atlases/mobile/boostedJackpotIconBoard.json?v=20260513-1',
  },
};

type Platform = 'mobile' | 'web';

const createThemeAsset = (name: string, folderPath: string, fileName: string, platform: Platform) => ({
  name,
  path: `frame/frameModuleResources/boostedJackpot/wheelThemes/${folderPath}/${platform}/${fileName}.json`,
  config: {
    metadata: {
      spineAtlasFile: `frame/frameModuleResources/boostedJackpot/wheelThemes/${folderPath}/${platform}/${fileName}.atlas`,
    },
  },
});

const getDefaultBoostedWheelThemeResources = (platform: Platform) => ({
  boostedWheelThemeBg: createThemeAsset('boostedWheelThemeBg', 'Bg', 'background', platform),
  boostedWheelThemeRays: createThemeAsset('boostedWheelThemeRays', 'Rays', 'rays', platform),
  boostedWheelThemeFrame: createThemeAsset('boostedWheelThemeFrame', 'Wheel', 'wheel', platform),
  boostedWheelThemeArrow: createThemeAsset('boostedWheelThemeArrow', 'default/Arrow', 'arrow', platform),
  boostedWheelThemeLogo: createThemeAsset('boostedWheelThemeLogo', 'default/Logo', 'logo', platform),
  boostedWheelThemeButton: createThemeAsset('boostedWheelThemeButton', 'default/Button', 'button', platform),
});

const getBoostedWheelThemeResources = (platform: Platform) => ({
  [BoostedWheelTheme.IChancy]: {
    boostedWheelThemeArrow: createThemeAsset('boostedWheelThemeArrow', 'ichancy/Arrow', 'arrow', platform),
    boostedWheelThemeLogo: createThemeAsset('boostedWheelThemeLogo', 'ichancy/Logo', 'logo', platform),
    boostedWheelThemeButton: createThemeAsset('boostedWheelThemeButton', 'ichancy/Button', 'button', platform),
  },
  [BoostedWheelTheme.Bet55]: {
    boostedWheelThemeArrow: createThemeAsset('boostedWheelThemeArrow', '55bet/Arrow', 'arrow', platform),
    boostedWheelThemeLogo: createThemeAsset('boostedWheelThemeLogo', '55bet/Logo', 'logo', platform),
    boostedWheelThemeButton: createThemeAsset('boostedWheelThemeButton', '55bet/Button', 'button', platform),
  },
} as const);

const createWheelIconAsset = (relativePath: string) => ({
  name: 'wheelIcon',
  path: `frame/frameModuleResources/boostedJackpot/wheelThemes/${relativePath}/wheelIcon.json`,
  config: {
    metadata: {
      spineAtlasFile: `frame/frameModuleResources/boostedJackpot/wheelThemes/${relativePath}/wheelIcon.atlas`,
    },
  },
});

const boostedWheelIconResources = {
  default: {
    web: createWheelIconAsset('default/wheelIcon/web'),
    mobile: createWheelIconAsset('default/wheelIcon/mobile'),
  },
  [BoostedWheelTheme.IChancy]: {
    web: createWheelIconAsset('ichancy/wheelIcon/web'),
    mobile: createWheelIconAsset('ichancy/wheelIcon/mobile'),
  },
  [BoostedWheelTheme.Bet55]: {
    web: createWheelIconAsset('55bet/wheelIcon/web'),
    mobile: createWheelIconAsset('55bet/wheelIcon/mobile'),
  },
};

const getBoostedWheelParticleResources = (platform: Platform) => ({
  boostedWheelParticlesCoinJump: {
    name: 'boostedWheelParticlesCoinJump',
    path: `frame/frameModuleResources/boostedJackpot/particles/coins/${platform}/coinJump.json`,
  },
  boostedWheelParticlesCoinDrop: {
    name: 'boostedWheelParticlesCoinDrop',
    path: `frame/frameModuleResources/boostedJackpot/particles/coins/${platform}/coinDrop.json`,
  },
  boostedWheelParticlesWheelPart: {
    name: 'boostedWheelParticlesWheelPart',
    path: `frame/frameModuleResources/boostedJackpot/particles/wheel/${platform}/wheelPart.json`,
  },
});

const getBoostedWheelTextureResources = (platform: Platform) => ({
  boostedWheelSectorTexture: {
    name: 'boostedWheelSectorTexture',
    path: `frame/frameModuleResources/boostedJackpot/wheelThemes/sector/${platform}/sector-texture.png`,
  },
});

const getBoostedWheelCongratsResources = (platform: Platform) => ({
  boostedCongratsAmount: {
    name: 'boostedCongratsAmount',
    path: `frame/frameModuleResources/boostedJackpot/boostedJackpotAtlases/congratsAmount/${platform}/amount.json`,
    config: {
      metadata: {
        spineAtlasFile: `frame/frameModuleResources/boostedJackpot/boostedJackpotAtlases/congratsAmount/${platform}/amount.atlas`,
      },
    },
  },
});

export const getBoostedJackpotResources = (partnerId: number) => {
  const mobile = isMobile();
  const platform: Platform = mobile ? 'mobile' : 'web';
  const partnerWheelIcon = boostedWheelIconResources[partnerId as keyof typeof boostedWheelIconResources]?.[platform];
  const wheelIconResource = partnerWheelIcon || boostedWheelIconResources.default[platform];

  const defaultBoostedWheelThemeResources = getDefaultBoostedWheelThemeResources(platform);
  const boostedWheelThemeResources = getBoostedWheelThemeResources(platform);
  const boostedWheelParticleResources = getBoostedWheelParticleResources(platform);
  const boostedWheelTextureResources = getBoostedWheelTextureResources(platform);
  const boostedWheelCongratsResources = getBoostedWheelCongratsResources(platform);

  const themeResources = {
    ...defaultBoostedWheelThemeResources,
    ...boostedWheelThemeResources[partnerId as keyof typeof boostedWheelThemeResources],
  };

  const sceneSpineResources = {
    ...(mobile ? mobileSceneSpineResources : webSceneSpineResources),
    wheelIcon: wheelIconResource,
  };

  return [
    ...Object.values(sceneSpineResources),
    ...Object.values(mobile ? mobileAtlases : webAtlases),
    ...Object.values(themeResources),
    ...Object.values(boostedWheelParticleResources),
    ...Object.values(boostedWheelTextureResources),
    ...Object.values(boostedWheelCongratsResources),
  ];
};
